/**
 * WebSocket client with reconnect and mandatory re-sync.
 *
 * The socket is a notification channel, never the source of truth. Every
 * (re)connection reports `resync` so the caller re-reads its snapshot over REST;
 * a dropped socket therefore cannot leave the UI showing stale call state.
 */

import { websocketUrl } from './api/client';
import type { RealtimeMessage } from './api/types';

export type ConnectionStatus = 'connecting' | 'online' | 'reconnecting' | 'offline';

export interface RealtimeHandlers {
  onMessage: (message: RealtimeMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
  onResync: () => void;
}

const MAX_BACKOFF_MS = 15000;

export function connectRealtime(topics: string[], handlers: RealtimeHandlers): () => void {
  let socket: WebSocket | null = null;
  let attempt = 0;
  let closed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    if (closed) return;
    handlers.onStatus(attempt === 0 ? 'connecting' : 'reconnecting');
    try {
      socket = new WebSocket(websocketUrl(topics));
    } catch {
      scheduleRetry();
      return;
    }

    socket.onopen = () => {
      attempt = 0;
      handlers.onStatus('online');
      // Authoritative state always comes from REST, including after a reconnect.
      handlers.onResync();
    };

    socket.onmessage = (event) => {
      try {
        handlers.onMessage(JSON.parse(event.data as string) as RealtimeMessage);
      } catch {
        // A malformed frame must never take the workspace down.
      }
    };

    socket.onerror = () => {
      socket?.close();
    };

    socket.onclose = () => {
      if (closed) return;
      handlers.onStatus('reconnecting');
      scheduleRetry();
    };
  };

  const scheduleRetry = () => {
    attempt += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(attempt, 5));
    timer = setTimeout(open, delay);
  };

  open();

  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    handlers.onStatus('offline');
    socket?.close();
  };
}
